import { setToken } from '@/core/token';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Landing route on a custom domain after central login:
 * /auth/handoff?code=<one-time code>&path=<encoded app path>
 * Redeems the code for a JWT, stores it host-scoped, then enters the app.
 */
export default function HandoffPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // strict-mode double-invoke guard — the code is single-use
    ran.current = true;
    const code = params.get('code');
    const path = params.get('path') || '/';
    if (!code) {
      setFailed(true);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_ENDPOINT}/custom-domain/redeem`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        const data = await res.json();
        if (!res.ok || !data.token) throw new Error(data.error || 'REDEEM_FAILED');
        setToken(data.token);
        // Only accept a single leading-slash relative path — reject protocol-relative
        // (`//evil.com`) and backslash-prefixed (`/\evil.com`) values that browsers
        // treat as absolute/scheme-relative URLs, which would send the user off-site.
        navigate(/^\/(?!\/|\\)/.test(path) ? path : '/', { replace: true });
      } catch {
        setFailed(true);
      }
    })();
  }, [params, navigate]);

  if (failed) {
    // Expired/used code — restart the login round-trip.
    window.location.replace('/auth/login');
    return null;
  }
  return (
    <div className='flex h-screen items-center justify-center'>
      <Loader2 className='h-6 w-6 animate-spin' />
    </div>
  );
}
